import { useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment";

import {
  clearLocalStorageFieldFilterOptions,
  clearLocalStorageFieldSearchQuery,
  createFieldSearchMatcher,
  getLocalStorageFieldSearchQuery,
  setLocalStorageFieldSearchQuery,
} from "utils";

import { FieldListField, FieldShape, ItemsWithMissingData, ItemsWithMissingDataByFieldId } from "models";

import { DATA_STATUS } from "pages/app/all-field-view/components/FieldFilterOptions";
import { FieldStatus } from "pages/app/all-field-view/hooks/useFieldStatusMap";

const EMPTY_MISSING_DATA: ItemsWithMissingData = {
  cropsWithMissingGrowingDates: [],
  cropsWithMissingPractices: [],
  practicesWithMissingData: [],
  missingCrops: [],
  cropsWithDataToConfirm: [],
  cropsWithVerificationEditingRequests: [],
  coverCropsWithMissingRedzoneException: [],
  cropsWithMissingSecondaryDataStatus: [],
};

export const useMapViewFilters = ({
  fields,
  fieldShapes,
  fieldStatusMap,
  farmId,
  hasNoFields,
}: {
  fields: FieldListField[] | undefined;
  fieldShapes: FieldShape[];
  fieldStatusMap: Record<string, FieldStatus>;
  farmId: string | undefined;
  hasNoFields: boolean;
}) => {
  const [filteredFieldIds, setFilteredFieldIds] = useState<string[] | null>(null);
  const [appliedCropIds, setAppliedCropIds] = useState<string[]>([]);
  const [appliedYears, setAppliedYears] = useState<number[]>([]);
  const [appliedDataStatus, setAppliedDataStatus] = useState<DATA_STATUS>(DATA_STATUS.ALL);
  const [searchQuery, setSearchQuery] = useState(() => (farmId ? getLocalStorageFieldSearchQuery(farmId) : ""));
  const [selectedFilterOptionsCount, setSelectedFilterOptionsCount] = useState(0);

  const itemsWithMissingDataByFieldId = useMemo(
    (): ItemsWithMissingDataByFieldId =>
      Object.fromEntries(
        Object.entries(fieldStatusMap).map(([fieldId, status]) => [
          fieldId,
          status.isComplete ? EMPTY_MISSING_DATA : { ...EMPTY_MISSING_DATA, cropsWithMissingPractices: [{ id: fieldId, startTime: moment() }] },
        ])
      ),
    [fieldStatusMap]
  );

  const handleClearFilters = useCallback(() => {
    setFilteredFieldIds(null);
    setSearchQuery("");
    setSelectedFilterOptionsCount(0);
    setAppliedCropIds([]);
    setAppliedYears([]);
    setAppliedDataStatus(DATA_STATUS.ALL);
    clearLocalStorageFieldFilterOptions();
    clearLocalStorageFieldSearchQuery();
  }, []);

  // Restore search query from localStorage when farm is known
  useEffect(() => {
    if (!farmId) return;
    const saved = getLocalStorageFieldSearchQuery(farmId);
    if (saved) setSearchQuery(saved);
  }, [farmId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist search query to localStorage on change
  useEffect(() => {
    if (!farmId) return;
    setLocalStorageFieldSearchQuery(farmId, searchQuery);
  }, [searchQuery, farmId]);

  const matchesSearch = useMemo(() => createFieldSearchMatcher(searchQuery), [searchQuery]);

  const filteredFields = useMemo(() => {
    const availableFields = filteredFieldIds === null ? fields : fields?.filter((f) => filteredFieldIds.includes(f.id));
    return availableFields?.filter(matchesSearch) ?? [];
  }, [fields, matchesSearch, filteredFieldIds]);

  const filteredFieldIdSet = useMemo(() => new Set(filteredFields.map((f) => f.id)), [filteredFields]);

  const filteredFieldShapes = useMemo(() => fieldShapes.filter((s) => filteredFieldIdSet.has(s.fieldId)), [fieldShapes, filteredFieldIdSet]);

  const hasNoFilteredFields = !hasNoFields && filteredFields.length === 0 && (!!searchQuery || selectedFilterOptionsCount > 0);

  return {
    filteredFieldIds,
    setFilteredFieldIds,
    appliedCropIds,
    setAppliedCropIds,
    appliedYears,
    setAppliedYears,
    appliedDataStatus,
    setAppliedDataStatus,
    searchQuery,
    setSearchQuery,
    setSelectedFilterOptionsCount,
    itemsWithMissingDataByFieldId,
    filteredFields,
    filteredFieldShapes,
    hasNoFilteredFields,
    handleClearFilters,
  };
};
